import React, { useCallback, useMemo, useState } from "react";
import { FiRefreshCcw, FiFilter } from "react-icons/fi";
import { useInvalidate } from "../../../../hooks/useInvalidate";
import Divider from "../../../../components/shared/Divider/Divider";
import { Pagination } from "../../../../components/shared/Pagination/Pagination";
import Button from "../../../../components/shared/Button/Button";
import SelectMulti from "../../../../components/shared/Select/SelectMulti";
import { useAdminSearchStore } from "../../store/adminSearchStore";
import { useAdminSearchSessions } from "../../hooks/useAdminSearchSessions";
import { SessionsFiltersPanel } from "./SessionsFiltersPanel";
import {
  SESSIONS_FIXED_COLUMNS,
  sessionsColumnOptions,
} from "./constants/columns";
import { createSessionsColumnDefinitions } from "./SessionsTableColumns";
import EditSessionModal from "./EditSessionModal";

export default function SessionsView() {
  const invalidate = useInvalidate();
  const {
    sessionsState,
    setSessionsPagination,
    setSessionsVisibleColumns,
    setSessionsFiltersOpen,
    setSelectedSessionId,
  } = useAdminSearchStore();

  const { filters, pagination, visibleColumns, filtersOpen } = sessionsState;

  const { data, isLoading, error } = useAdminSearchSessions();
  const sessions = data?.sessions || [];
  const total = data?.total || 0;
  const [isEditingModalOpen, setIsEditingModalOpen] = useState(false);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || 0) / (pagination.limit || 1))),
    [total, pagination.limit]
  );

  const onPageChange = useCallback(
    async (page) => {
      const clamped = Math.min(Math.max(page, 1), totalPages);
      setSessionsPagination({ page: clamped });
    },
    [totalPages, setSessionsPagination]
  );

  const onRowsPerPageChange = useCallback(
    async (n) => {
      setSessionsPagination({ page: 1, limit: n });
    },
    [setSessionsPagination]
  );

  const onRefresh = useCallback(() => {
    invalidate(["adminSearchSessions_key"]);
  }, [invalidate]);

  const handleEditSession = useCallback(
    (sessionId) => {
      setIsEditingModalOpen(true);
      setSelectedSessionId(sessionId);
    },
    [setSelectedSessionId]
  );

  const handleCloseEdit = useCallback(() => {
    setIsEditingModalOpen(false);
    setSelectedSessionId(null);
  }, [setSelectedSessionId]);

  const handleFiltersSubmit = useCallback(() => {
    setSessionsPagination({ page: 1 });
    setSessionsFiltersOpen(false);
    invalidate(["adminSearchSessions_key"]);
  }, [invalidate, setSessionsFiltersOpen, setSessionsPagination]);

  // Column definitions
  const columnDefinitions = useMemo(
    () => createSessionsColumnDefinitions(),
    []
  );

  const columnHandlers = useMemo(
    () => ({
      onEditSession: handleEditSession,
    }),
    [handleEditSession]
  );

  const visibleColumnDefs = useMemo(() => {
    // Always include the fixed columns first
    const fixedCols = SESSIONS_FIXED_COLUMNS.map(
      (colKey) => columnDefinitions[colKey]
    ).filter(Boolean);
    const otherCols = visibleColumns
      .filter(
        (colKey) =>
          !SESSIONS_FIXED_COLUMNS.includes(colKey) && columnDefinitions[colKey]
      )
      .map((colKey) => columnDefinitions[colKey]);
    // Return fixed columns first, then other selected columns
    return [...fixedCols, ...otherCols];
  }, [visibleColumns, columnDefinitions]);

  const columnCount = visibleColumnDefs.length || 1;

  const hasActiveFilters = useMemo(() => {
    const { sessionName, sessionDate } = filters || {};
    const hasName = Boolean((sessionName || "").trim());
    const hasDate = Boolean(sessionDate?.from) || Boolean(sessionDate?.to);
    return hasName || hasDate;
  }, [filters]);

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          onClick={() => setSessionsFiltersOpen(true)}
          color={hasActiveFilters ? "primary50" : "white/gray300"}
          size="md"
          rounded="lg"
          startContent={<FiFilter />}
          title="Open filters"
        >
          Filters
        </Button>
        <SelectMulti
          header="Columns"
          className="min-w-[192px]"
          onChange={(val) => {
            if (SESSIONS_FIXED_COLUMNS.includes(val)) return;
            setSessionsVisibleColumns((prev = SESSIONS_FIXED_COLUMNS) => {
              const filtered = prev.filter(
                (v) => !SESSIONS_FIXED_COLUMNS.includes(v)
              );
              return filtered.includes(val)
                ? filtered.filter((v) => v !== val)
                : [...filtered, val];
            });
          }}
          onClear={() => setSessionsVisibleColumns([...SESSIONS_FIXED_COLUMNS])}
          options={sessionsColumnOptions}
          placeholder="Select columns..."
          values={(visibleColumns || []).filter(
            (v) => !SESSIONS_FIXED_COLUMNS.includes(v)
          )}
          buttonProps={{
            size: "md",
            rounded: "lg",
            color:
              (visibleColumns || []).filter(
                (v) => !SESSIONS_FIXED_COLUMNS.includes(v)
              ).length > 0
                ? "primary50"
                : "white/gray300",
          }}
        />
        <Button
          type="button"
          isDisabled={isLoading}
          onClick={onRefresh}
          color="white/gray300"
          size="md"
          rounded="lg"
          startContent={<FiRefreshCcw />}
          title="Refresh"
        >
          Refresh
        </Button>
      </div>

      <SessionsFiltersPanel
        isOpen={filtersOpen}
        onClose={() => setSessionsFiltersOpen(false)}
        onSubmit={handleFiltersSubmit}
      />

      <div className="overflow-hidden rounded-xl border border-gray200">
        <div className="overflow-x-auto min-h-80">
          <table className="min-w-full text-left text-base">
            <thead className="bg-gray50 text-sm uppercase tracking-wide text-gray600">
              <tr>{visibleColumnDefs.map((col) => col.renderHeader())}</tr>
            </thead>

            <tbody className="divide-y divide-gray200">
              {error && (
                <tr>
                  <td
                    colSpan={columnCount}
                    className="px-6 py-10 text-center text-base text-red-700"
                  >
                    {error?.message || "Failed to fetch users"}
                  </td>
                </tr>
              )}
              {isLoading && (
                <tr>
                  <td
                    colSpan={columnCount}
                    className="px-6 py-10 text-center text-base text-gray500"
                  >
                    Loading…
                  </td>
                </tr>
              )}
              {!isLoading && sessions.length === 0 && !error && (
                <tr>
                  <td
                    colSpan={columnCount}
                    className="px-6 py-10 text-center text-base text-gray500"
                  >
                    No results
                  </td>
                </tr>
              )}

              {sessions.map((s, index) => {
                return (
                  <tr key={s.id} className={index % 2 === 1 ? "bg-gray50" : ""}>
                    {visibleColumnDefs.map((col) => (
                      <React.Fragment key={col.key}>
                        {col.renderCell(s, columnHandlers)}
                      </React.Fragment>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <Divider color="gray200" type="horizontal" />

        <Pagination
          currentPage={pagination.page}
          pageNumber={totalPages}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPage={pagination.limit}
          showItemsPerPage={sessions.length > 0}
          size="lg"
          className="px-6 py-4"
        />
      </div>

      <EditSessionModal isOpen={isEditingModalOpen} onClose={handleCloseEdit} />
    </>
  );
}
